import { createContext, useCallback, useContext } from 'react';

interface AnnouncementContextValue {
  announce: (text: string) => void;
}

const AnnouncementContext = createContext<AnnouncementContextValue>({
  announce: () => {},
});

export function useAnnouncement(): AnnouncementContextValue {
  return useContext(AnnouncementContext);
}

interface AnnouncementProviderProps {
  children: React.ReactNode;
}

/**
 * Provides the `announce()` function used to push plain-text messages into the
 * screen-reader live region (#sr-announcements in index.html).
 *
 * The clear-then-rAF pattern is required to re-trigger announcements when the
 * same text is sent twice (e.g. identical bot responses). Without clearing first,
 * NVDA and JAWS silently skip repeated content.
 */
export function AnnouncementProvider({ children }: AnnouncementProviderProps) {
  const announce = useCallback((text: string) => {
    const el = document.getElementById('sr-announcements');
    if (!el) return;
    el.textContent = '';
    requestAnimationFrame(() => {
      el.textContent = text;
    });
  }, []);

  return (
    <AnnouncementContext.Provider value={{ announce }}>{children}</AnnouncementContext.Provider>
  );
}
